import { useMemo, useState } from 'react';

import { ChevronDownIcon, ChevronsUpDownIcon, ChevronUpIcon, InfoIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { Link } from 'react-router';

import { InfoTooltip } from '@/components/atoms/InfoTooltip/InfoTooltip';
import { TruncatedText } from '@/components/atoms/TruncatedText/TruncatedText';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useComparisonMode } from '@/modules/analytics/hooks/useComparisonMode';
import { selectAnalyticsDates } from '@/modules/analytics/redux/selectors';
import { aiAgentLink } from '@/modules/analytics/utils/analyticsLinks';
import { selectAccountSlug } from '@/redux/session/selectors';

import { AiAgentsHallucinationExport } from './AiAgentsHallucinationExport';
import { AnalyticsEmptyState } from './AnalyticsEmptyState';
import { CompareTrendCell } from './CompareTrendCell';
import {
  type AiAgentHallucinationRow,
  useAiAgentsHallucinations,
} from './hooks/useAiAgentsHallucinations';
import { formatDateRangeLabel } from '../../utils/formatDateRangeLabel';

type SortColumn =
  'agent' | 'totalMessages' | 'detectedFactual' | 'detectedJailbreak' | 'detectedOutOfScope';
type SortDirection = 'asc' | 'desc';

const numberFormatter = new Intl.NumberFormat('en-US');

const SortIndicator = ({ active, direction }: { active: boolean; direction?: SortDirection }) => {
  if (!active) {
    return <ChevronsUpDownIcon className="size-3.5 text-muted-foreground" />;
  }
  const DirectionIcon = direction === 'asc' ? ChevronUpIcon : ChevronDownIcon;
  return <DirectionIcon className="size-3.5 text-foreground" />;
};

const HeaderButton = ({
  column,
  label,
  activeColumn,
  direction,
  onSort,
}: {
  column: SortColumn;
  label: string;
  activeColumn?: SortColumn;
  direction?: SortDirection;
  onSort: (column: SortColumn) => void;
}) => (
  <button
    type="button"
    onClick={() => onSort(column)}
    className="inline-flex items-center gap-1 text-left text-sm leading-6 font-medium text-muted-foreground hover:text-foreground"
  >
    {label}
    <SortIndicator active={activeColumn === column} direction={direction} />
  </button>
);

const compareByColumn = (
  a: AiAgentHallucinationRow,
  b: AiAgentHallucinationRow,
  column: SortColumn
): number => {
  switch (column) {
    case 'agent':
      return a.name.localeCompare(b.name);
    case 'totalMessages':
      return a.totalMessages - b.totalMessages;
    case 'detectedFactual':
      return a.detectedFactual - b.detectedFactual;
    case 'detectedJailbreak':
      return a.detectedJailbreak - b.detectedJailbreak;
    case 'detectedOutOfScope':
      return a.detectedOutOfScope - b.detectedOutOfScope;
    default:
      return 0;
  }
};

const DetectionCell = ({ detected }: { detected: number }) => (
  <TableCell className="h-14 w-36 min-w-24 px-4 text-sm leading-5 font-normal text-foreground tabular-nums">
    {numberFormatter.format(detected)}
  </TableCell>
);

const AgentCell = ({ row }: { row: AiAgentHallucinationRow }) => {
  const { t } = useTranslation();
  const slug = useSelector(selectAccountSlug);
  const exists = !row.isMissing;
  const content = (
    <>
      <span className="flex size-4 shrink-0 items-center justify-center text-muted-foreground [&_svg]:size-4">
        {row.icon}
      </span>
      <TruncatedText
        text={exists ? row.name : t('analytics.deleted_ai_agent')}
        size="large"
        className="text-sm leading-none font-medium text-foreground"
      />
    </>
  );

  if (!slug || !exists) {
    return <div className="flex min-w-0 items-center gap-2">{content}</div>;
  }

  return (
    <Link
      to={aiAgentLink(slug, row.id)}
      className="flex min-w-0 items-center gap-2 hover:underline"
    >
      {content}
    </Link>
  );
};

export function AiAgentsHallucinationTable() {
  const { t } = useTranslation();
  const { data: hallucinations, isLoading, error } = useAiAgentsHallucinations();
  const { isComparing } = useComparisonMode();
  const { startDate, endDate } = useSelector(selectAnalyticsDates);
  const [sortBy, setSortBy] = useState<SortColumn | undefined>(undefined);
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');
  const hasError = !isLoading && error !== null;
  const isEmpty = !isLoading && !hasError && hallucinations.length === 0;

  const dateRange = useMemo(() => formatDateRangeLabel(startDate, endDate), [startDate, endDate]);

  const rows = useMemo(() => {
    if (!sortBy) {
      return hallucinations;
    }
    const next = [...hallucinations].sort((a, b) => compareByColumn(a, b, sortBy));
    return sortDirection === 'asc' ? next : next.reverse();
  }, [hallucinations, sortBy, sortDirection]);

  const onSort = (column: SortColumn) => {
    if (sortBy === column) {
      setSortDirection((d) => (d === 'asc' ? 'desc' : 'asc'));
      return;
    }
    setSortBy(column);
    setSortDirection('asc');
  };

  return (
    <section className="flex flex-col gap-6 rounded-lg border border-border bg-background p-6">
      <header className="flex items-start gap-4">
        <div className="flex min-w-0 flex-1 flex-col gap-2">
          <div className="flex items-center gap-1.5">
            <h2 className="text-base leading-none font-semibold text-foreground">
              {t('analytics.ai_agents_hallucination_title')}
            </h2>
            <InfoTooltip content={t('analytics.ai_agents_hallucination_description')} size="large">
              <InfoIcon className="size-4 text-muted-foreground" />
            </InfoTooltip>
          </div>
          <p className="text-sm leading-5 font-normal text-muted-foreground">{dateRange}</p>
        </div>
        {!isEmpty && !hasError && (
          <AiAgentsHallucinationExport data={hallucinations} disabled={isLoading} />
        )}
      </header>

      {(hasError || isEmpty) && (
        <div className="flex items-center justify-center py-6">
          <AnalyticsEmptyState
            title={hasError ? t('analytics.failed_to_load_data') : t('analytics.no_data_available')}
            description={
              hasError ? t('analytics.try_again_later') : t('analytics.no_data_for_period')
            }
          />
        </div>
      )}
      {!hasError && !isEmpty && (
        <Table className="max-h-96 overscroll-x-none overscroll-y-auto">
          <TableHeader>
            <TableRow className="h-12 hover:bg-transparent">
              <TableHead className="h-12 px-4 py-1">
                <HeaderButton
                  column="agent"
                  label={t('analytics.ai_agent')}
                  activeColumn={sortBy}
                  direction={sortDirection}
                  onSort={onSort}
                />
              </TableHead>
              <TableHead className="h-12 w-36 min-w-24 px-4 py-1">
                <HeaderButton
                  column="totalMessages"
                  label={t('analytics.totalMessages')}
                  activeColumn={sortBy}
                  direction={sortDirection}
                  onSort={onSort}
                />
              </TableHead>
              <TableHead className="h-12 w-36 min-w-24 px-4 py-1 whitespace-normal">
                <HeaderButton
                  column="detectedFactual"
                  label={t('analytics.ai_agents_hallucination_factual')}
                  activeColumn={sortBy}
                  direction={sortDirection}
                  onSort={onSort}
                />
              </TableHead>
              <TableHead className="h-12 w-36 min-w-24 px-4 py-1">
                <HeaderButton
                  column="detectedJailbreak"
                  label={t('analytics.ai_agents_hallucination_jailbreak')}
                  activeColumn={sortBy}
                  direction={sortDirection}
                  onSort={onSort}
                />
              </TableHead>
              <TableHead className="h-12 w-36 min-w-24 px-4 py-1 whitespace-normal">
                <HeaderButton
                  column="detectedOutOfScope"
                  label={t('analytics.ai_agents_hallucination_out_of_scope')}
                  activeColumn={sortBy}
                  direction={sortDirection}
                  onSort={onSort}
                />
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading && rows.length === 0
              ? Array.from({ length: 5 }, (_, i) => (
                  <TableRow key={`skeleton-${i}`} className="h-14">
                    <TableCell className="h-14 px-4">
                      <Skeleton className="h-5 w-48" />
                    </TableCell>
                    <TableCell className="h-14 w-36 min-w-24 px-4">
                      <Skeleton className="h-5 w-20" />
                    </TableCell>
                    <TableCell className="h-14 w-36 min-w-24 px-4">
                      <Skeleton className="h-5 w-12" />
                    </TableCell>
                    <TableCell className="h-14 w-36 min-w-24 px-4">
                      <Skeleton className="h-5 w-12" />
                    </TableCell>
                    <TableCell className="h-14 w-36 min-w-24 px-4">
                      <Skeleton className="h-5 w-12" />
                    </TableCell>
                  </TableRow>
                ))
              : rows.map((row) => (
                  <TableRow key={row.id} className="h-14">
                    <TableCell className="h-14 max-w-0 px-4">
                      <AgentCell row={row} />
                    </TableCell>
                    <TableCell className="h-14 w-36 min-w-24 px-4 text-sm leading-5 font-normal text-foreground tabular-nums">
                      <div className="inline-flex items-center gap-1.5">
                        {isComparing && (
                          <CompareTrendCell
                            current={row.totalMessages}
                            previous={row.previousTotalMessages}
                            formatValue={(value) => numberFormatter.format(value)}
                            invertColors
                          />
                        )}
                        <span>
                          {numberFormatter.format(row.totalMessages)}{' '}
                          <span className="text-muted-foreground">
                            ({row.detectedPercentLabel})
                          </span>
                        </span>
                      </div>
                    </TableCell>
                    <DetectionCell detected={row.detectedFactual} />
                    <DetectionCell detected={row.detectedJailbreak} />
                    <DetectionCell detected={row.detectedOutOfScope} />
                  </TableRow>
                ))}
          </TableBody>
        </Table>
      )}
    </section>
  );
}
